import React, { useState, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled, { keyframes } from 'styled-components';
import { navDelay, loaderDelay } from '@utils';
import { usePrefersReducedMotion } from '@hooks';
import resume from '@images/Keerthan_K_Acharya.pdf';



const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  align-items: center;
  min-height: calc(100vh - var(--nav-height) - 80px);
  padding: 0;
  position: relative;
  text-align: center;
  width: 100vw;
  max-width: 100vw;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
  margin-bottom: 120px;
  
  .content-wrapper {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;

    @media (max-width: 480px) {
      padding: 0 20px;
    }
  }

  h1 {
    font-size: var(--fz-massive);
    font-weight: 800;
    letter-spacing: -0.04em;
    line-height: 0.9;
    color: var(--text-main);
    text-transform: uppercase;
    margin: 0;

    @media (max-width: 480px) {
      font-size: clamp(32px, 10vw, 36px);
    }
    
    .outline {
      color: transparent;
      -webkit-text-stroke: 1px var(--text-dark);
      display: block;
      transition: var(--transition);
      &:hover {
        color: var(--accent-main);
        -webkit-text-stroke: 1px var(--accent-main);
        text-shadow: var(--accent-glow);
      }
    }
  }

  .subtitle {
    margin-top: 30px;
    font-family: var(--font-mono);
    color: var(--accent-main);
    font-size: var(--fz-lg);
    letter-spacing: 0.1em;
    text-transform: uppercase;

    @media (max-width: 480px) {
      font-size: var(--fz-sm);
      margin-top: 20px;
    }
  }

  p {
    margin-top: 20px;
    max-width: 600px;
    font-size: var(--fz-xl);
    color: var(--text-muted);
    font-weight: 400;

    @media (max-width: 768px) {
      font-size: var(--fz-lg);
    }

    @media (max-width: 480px) {
      font-size: var(--fz-md);
    }
  }

  .actions {
    margin-top: 50px;
    display: flex;
    gap: 20px;

    @media (max-width: 480px) {
      flex-direction: column;
      width: 100%;
      padding: 0 20px;
      
      a {
        width: 100%;
      }
    }

    a.primary {
      ${({ theme }) => theme.mixins.bigButton};
    }
    
    a.secondary {
      ${({ theme }) => theme.mixins.bigButton};
      background: transparent;
      border: 1px solid var(--text-dark);
      color: var(--text-main);
      &:hover {
        border-color: var(--text-main);
        background: var(--surface-light);
        box-shadow: none;
      }
    }
  }
`;

const Hero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => setIsMounted(true), navDelay);
    return () => clearTimeout(timeout);
  }, []);

  const title = (
    <h1>
      <span className="outline">Keerthan</span>
      <span>K Acharya</span>
    </h1>
  );
  
  const subtitle = <div className="subtitle">Founding Engineer</div>;

  const desc = (
    <p>
      Founding Engineer building AI systems, cloud infrastructure, and products at scale.
      I design and ship production software across full-stack engineering and autonomous AI agents. 
    </p>
  );

  const actions = (
    <div className="actions">
      <a className="primary" href="mailto:keerthankacharya@gmail.com">
        Get In Touch
      </a>
      <a className="secondary" href={resume} target="_blank" rel="noopener noreferrer">
        Resume
      </a>
    </div>
  );

  const items = [title, subtitle, desc, actions];

  return (
    <StyledHeroSection>
      <div className="content-wrapper">
        {prefersReducedMotion ? (
          <>
            {items.map((item, i) => (
              <div key={i}>{item}</div>
            ))}
          </>
        ) : (
          <TransitionGroup component={null}>
            {isMounted &&
              items.map((item, i) => (
                <CSSTransition key={i} classNames="fadeup" timeout={loaderDelay}>
                  <div style={{ transitionDelay: `${i + 1}00ms` }}>{item}</div>
                </CSSTransition>
              ))}
          </TransitionGroup>
        )}
      </div>
    </StyledHeroSection>
  );
};

export default Hero;
